/**
 * Pure helpers for the notifications drawer (plan 4 R11).
 */

export const NOTIF_TABS = [
  { id: 'all', label: 'All' },
  { id: 'mention', label: 'Mentions' },
  { id: 'report', label: 'Reports' },
  { id: 'connector', label: 'Connectors' },
  { id: 'support', label: 'Support' },
];

/** Destination copy shown under an unread row. */
export const NOTIF_DESTINATIONS = {
  mention: 'Opens chat',
  report: 'Opens schedules',
  connector: 'Opens connectors',
  support: 'Opens support',
};

/**
 * @param {Array<{ id: string, label: string }>} [tabs]
 * @param {string} [role]
 */
export function visibleNotifTabs(tabs = NOTIF_TABS, role) {
  if (role === 'admin') return tabs.slice();
  return tabs.filter((tab) => tab.id !== 'connector');
}

/**
 * @param {Array<{ class?: string }>} rows
 * @param {string} tab
 */
export function filterNotifications(rows, tab) {
  const list = Array.isArray(rows) ? rows : [];
  if (!tab || tab === 'all') return list.slice();
  return list.filter((row) => row.class === tab);
}

/** @param {Array<{ read_at?: string|null }>} rows */
export function unreadCount(rows) {
  return (rows || []).filter((row) => !row.read_at).length;
}

/**
 * Mark one row read locally after POST /notifications/:id/read.
 * @param {Array<object>} rows
 * @param {string} id
 * @param {string} [iso]
 */
export function markRowRead(rows, id, iso) {
  const when = iso || new Date().toISOString();
  return (rows || []).map((row) => (
    row.id === id ? { ...row, read_at: row.read_at || when } : row
  ));
}

/** @param {Array<object>} rows @param {string} [iso] */
export function markAllRowsRead(rows, iso) {
  const when = iso || new Date().toISOString();
  return (rows || []).map((row) => ({ ...row, read_at: row.read_at || when }));
}

/**
 * Split classic backend titles into actor + rest for display.
 * @param {string|null|undefined} title
 * @returns {{ strong: string, rest: string }}
 */
export function splitNotifTitle(title) {
  const text = String(title || '').trim() || 'Notification';
  let match = text.match(/^(.+?) (mentioned you.*)$/);
  if (match) return { strong: match[1], rest: match[2] };
  match = text.match(/^Report ready:\s*(.+)$/);
  if (match) return { strong: match[1], rest: 'is ready' };
  match = text.match(/^(.+?) (is .*)$/);
  if (match) return { strong: match[1], rest: match[2] };
  return { strong: text, rest: '' };
}

/**
 * Classic deep links are hash routes; map them onto /app paths.
 * @param {string|null|undefined} deep
 * @param {string} [fallbackProductId]
 */
export function appPathFromNotifDeep(deep, fallbackProductId) {
  const raw = String(deep || '').replace(/^#/, '').trim();
  if (!raw || raw === '#') {
    return withPid('/app/dashboard', fallbackProductId);
  }
  if (raw === 'support-message' || raw.startsWith('support-message')) {
    return withPid('/app/settings/support', fallbackProductId);
  }
  const qIndex = raw.indexOf('?');
  const name = qIndex === -1 ? raw : raw.slice(0, qIndex);
  const params = new URLSearchParams(qIndex === -1 ? '' : raw.slice(qIndex + 1));
  const productId = params.get('product_id') || fallbackProductId || '';
  if (name === 'chat') {
    const group = params.get('group');
    return withPid(group ? `/app/chat/${encodeURIComponent(group)}` : '/app/chat', productId);
  }
  if (name === 'knowledge-base' || name === 'context') return withPid('/app/knowledge', productId);
  if (name === 'connectors') return withPid('/app/connectors', productId);
  if (name === 'schedules') return withPid('/app/schedules', productId);
  if (name === 'models') return withPid('/app/settings/models', productId);
  if (name === 'settings') {
    const section = params.get('section') || 'team';
    return withPid(`/app/settings/${encodeURIComponent(section)}`, productId);
  }
  if (name === 'dashboard' || name === 'first-read') return withPid('/app/dashboard', productId);
  return withPid('/app/dashboard', productId);
}

function withPid(path, productId) {
  if (!productId) return path;
  const [pathname, query = ''] = String(path).split('?');
  const params = new URLSearchParams(query);
  params.set('product_id', productId);
  const qs = params.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

/** @param {{ class?: string, title?: string, id?: string }} row */
export function notifGlyphKind(row) {
  if (row.class === 'mention') return 'mention';
  if (row.class === 'support') return 'support';
  if (row.class === 'report') return 'report';
  return 'connector';
}

/** @param {{ class?: string, title?: string }} row */
export function notifInitial(row) {
  if (row.class !== 'mention') return '';
  const actor = String(row.title || '').match(/^(.+?) mentioned you/);
  const name = actor ? actor[1] : '';
  return (name || '@').charAt(0).toUpperCase();
}
